import * as React from 'react';
import {
  requiredExternals,
  requiredServices,
  optionalServices,
  CostSimulatorFeatureApp,
  CostSimulatorFeatureAppConfig,
  CostSimulatorFeatureAppDependencies,
} from '@volkswagen-onehub/whitelabel-e-mobility-cost-feature-app';
import {
  BrandThemeProvider,
  ComponentsLibrary,
  createEMobilityToolsFeatureAppVariantTheme,
  EMobilityFeatureAppWrapper,
  isV26,
} from '@volkswagen-onehub/vw-e-mobility-components';
import {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import {FeatureAppLoader, ReactFeatureApp} from '@feature-hub/react';
import {
  createDevConfigs,
  customOptionalVwServices,
  fetchBffConfig,
  fetchTariffsBffConfig,
  getVwTrack,
  optionalVwServices,
  RequiredVwFeatureServices,
  requiredVwServices,
  VwFeatureServiceIds,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  NonSimulatorFeatureAppName,
  SimulatorFeatureAppName,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {
  CommonFeatureServices,
  defineEmobilityTariffsChannelService,
  CostFeatureAppContentConfig,
  filterValidIds,
  generateFeatureAppId,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {getCostTrackingFunctions} from './cost-tracking';
import {ThemeContext} from 'styled-components';
import {FaContentServiceV1} from '@volkswagen-onehub/fa-content-service';
import {ApplicationRoot} from '@volkswagen-onehub/components-core';

// Write debug data to global object

if (!window.eMobilityTools) {
  window.eMobilityTools = {};
}
window.eMobilityTools.cost = {
  version: process.env.APP_VERSION || 'unknown',
};

const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  CostSimulatorFeatureAppDependencies & RequiredVwFeatureServices,
  CostSimulatorFeatureAppConfig
> = {
  dependencies: {
    externals: requiredExternals,
    featureServices: {
      ...requiredVwServices,
      ...requiredServices,
    },
  },
  optionalDependencies: {
    featureServices: {
      ...optionalVwServices,
      ...optionalServices,
      ...customOptionalVwServices,
    },
  },
  ownFeatureServiceDefinitions: [defineEmobilityTariffsChannelService()],
  create: function (env): ReactFeatureApp {
    const localeService =
      env.featureServices[CommonFeatureServices.localeService];
    const appDataContent = env.featureServices[
      VwFeatureServiceIds.contentService
    ].content as CostFeatureAppContentConfig | undefined;

    const carlines = filterValidIds(appDataContent?.carlines);
    const appDataPromise = fetchBffConfig(
      SimulatorFeatureAppName.Cost,
      {
        localeService,
        dataVersion:
          env.featureServices[VwFeatureServiceIds.dataVersionService],
      },
      env.featureServices[VwFeatureServiceIds.serviceConfigEndpointProvider],
      env.baseUrl,
      {
        ...(carlines.length > 0 && {carlines}),
      }
    );

    const tariffsDataPromise = fetchTariffsBffConfig(
      {
        localeService,
        dataVersion:
          env.featureServices[VwFeatureServiceIds.dataVersionService],
      },
      env.featureServices[VwFeatureServiceIds.serviceConfigEndpointProvider],
      env.baseUrl
    );
    const loadingPromise = Promise.all([
      appDataPromise,
      tariffsDataPromise,
    ]).then(() => undefined);

    const vwTrack = getVwTrack(
      env.featureServices[VwFeatureServiceIds.trackingManager],
      localeService
    );

    const trackingFunctions = getCostTrackingFunctions(vwTrack);
    const tariffsFeatureAppId = generateFeatureAppId(
      env.featureAppId,
      NonSimulatorFeatureAppName.Tariffs,
      'feature-app'
    );
    const cleanBaseUrl = (env.baseUrl ?? '').replace(/\/$/, '');

    const srcPath = `${cleanBaseUrl}/assets/tariffs-feature-app/tariffs-feature-app.js`;

    const beforeCreateTariffs = () => {
      return (
        env: FeatureAppEnvironment<
          FeatureServices & {['fa-content-service']?: FaContentServiceV1},
          unknown
        >
      ): void => {
        const {featureServices} = env;
        const {'fa-content-service': faContentService} = featureServices ?? {};

        if (!faContentService || !appDataContent) {
          return;
        }

        faContentService.content = {
          isContentLayer: appDataContent.isContentLayer,
        };
      };
    };

    return {
      loadingPromise,
      render() {
        return (
          <ApplicationRoot>
            <BrandThemeProvider theme="main">
              <ThemeContext.Consumer>
                {(brandTheme) => (
                  <EMobilityFeatureAppWrapper>
                    <CostSimulatorFeatureApp
                      componentsLibrary={ComponentsLibrary}
                      theme={createEMobilityToolsFeatureAppVariantTheme(
                        brandTheme
                      )}
                      featureAppEnvironment={env}
                      appDataPromise={appDataPromise}
                      appDataContent={appDataContent}
                      tariffsDataPromise={tariffsDataPromise}
                      trackingFunctions={trackingFunctions}
                      externallyRenderedDisclaimerKind="module-reference"
                      channelTariffsId={tariffsFeatureAppId}
                      devConfig={createDevConfigs(isV26(brandTheme))}
                    >
                      <FeatureAppLoader
                        featureAppId={tariffsFeatureAppId}
                        baseUrl={env.baseUrl}
                        src={srcPath}
                        beforeCreate={beforeCreateTariffs()}
                      />
                    </CostSimulatorFeatureApp>
                  </EMobilityFeatureAppWrapper>
                )}
              </ThemeContext.Consumer>
            </BrandThemeProvider>
          </ApplicationRoot>
        );
      },
    };
  },
};

export default featureAppDefinition;
